import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Platform, AppState } from 'react-native';
import type { AppStateStatus } from 'react-native';
import { colors, textStyle } from './styles';
import StoryBuilder from './components/StoryBuilder';
import Gallery from './components/Gallery';
import SharedGallery from './components/SharedWithYouModal';
import ComicViewer from './components/ComicViewer';
import { cleanupExpiredImages } from './lib/imageCache';
import { useSocketStore } from './store/socketStore';
import { useFTUEStore } from './store/ftueStore';
import { useConfigStore } from './store/configStore';
import authService from './services/authService';
import TokenPrompt from './components/TokenPrompt';
import { initNetworkListeners } from './store/networkStore';
import FTUEFlow from './components/FTUEFlow';
import LandingPage from './components/LandingPage';
import GrownUpsBanner from './components/GrownUpsBanner';
import ErrorToast from './components/ErrorToast';
import { ACCESS_BLOCKED_TITLES } from './store/errorMessages';
import { trackView, View as MetricView } from './services/metrics';
import { useNavigation, useBrowserHistorySync } from './navigation/useNavigation';
import { useNavigationStore } from './navigation/navigationStore';
import { startBuilderSession } from './navigation/sessionActions';
import type { RouteName, RouteParams } from './navigation/routes';
// Game Coder (hackathon) — self-contained feature under src/coder/.
import CoderPage from './coder/CoderPage';
import GameLabScreen from './coder/GameLabScreen';
import GameTemplateScreen from './coder/GameTemplateScreen';
// Character Animator (hackathon) — self-contained feature under src/animator/.
import AnimatorPage from './animator/AnimatorPage';

// Routes whose surfaces are dark-themed, so global chrome (the error toast) matches.
const DARK_THEME_ROUTES: ReadonlySet<RouteName> = new Set(['gallery', 'sharedGallery', 'reader']);

export default function App() {
  const { route, navigate } = useNavigation();
  useBrowserHistorySync();

  const sessionTakenOver = useSocketStore((s) => s.sessionTakenOver);
  const accessBlocked = useSocketStore((s) => s.accessBlocked);
  const [needsManualAuth, setNeedsManualAuth] = useState(
    Platform.OS === 'web' && authService.isManualAuth() && !authService.hasManualCredentials()
  );
  const needsFTUE = useFTUEStore((s) => s.needsFTUE);
  const ftueLoaded = useFTUEStore((s) => s.isLoaded);
  const sharingEnabled = useConfigStore((s) => s.featureFlags.sharing);

  useEffect(() => { useFTUEStore.getState().load(); initNetworkListeners(); }, []);

  // FTUE is its own route, but a deep link (reader, or builder-with-project) is
  // shown first and defers FTUE so the link isn't clobbered. wasFtueRef tracks
  // the rising edge of "leaving FTUE" so the auto-advance fires once per FTUE
  // session (and re-arms for the dev "Show FTUE" reset).
  const onDeepLink =
    route.name === 'reader' ||
    // The Game Coder page is its own experience and has no FTUE of its own —
    // treat it as a deep link so an unfinished story-builder FTUE can't bounce
    // a visitor off /game-coder.
    route.name === 'coder' ||
    // The Game Lab demo screens (/game-lab, /game-lab/pick) lead into the coder page
    // and have no FTUE of their own — same reasoning as above.
    route.name === 'gameLab' ||
    route.name === 'gameTemplates' ||
    // Same for the Character Animator page (/animator).
    route.name === 'animator' ||
    (route.name === 'builder' && !!(route.params as RouteParams['builder']).project);
  const wasFtueRef = useRef(false);
  useEffect(() => {
    if (!ftueLoaded) return;
    if (needsFTUE) {
      if (onDeepLink) return; // defer FTUE; show the deep-linked screen
      if (route.name !== 'ftue') useNavigationStore.getState().replace('ftue');
      wasFtueRef.current = true;
    } else if (wasFtueRef.current) {
      wasFtueRef.current = false;
      startBuilderSession();
    }
  }, [needsFTUE, ftueLoaded, route.name, onDeepLink]);

  const appStateRef = useRef<AppStateStatus>(AppState.currentState);
  useEffect(() => {
    if (Platform.OS === 'web') return;

    const handler = (nextState: AppStateStatus) => {
      const prev = appStateRef.current;
      appStateRef.current = nextState;

      // During FTUE, skip socket/voice management — FTUEFlow has its own handler
      if (useFTUEStore.getState().needsFTUE) return;

      if (nextState === 'background' && prev !== 'background') {
        console.log('[App] Going into background');
        // Stop voice immediately when the app goes to background (device lock, home
        // button, app switch). Only on native as not needed on web and react-native-web's
        // AppState polyfill has compatibility issues with its EventEmitter
        useSocketStore.getState().stopVoice();
      }

      if (nextState === 'active' && prev !== 'active') {
        console.log('[App] Going into active state');
        const { socket, initSocket, cleanUpSocket } = useSocketStore.getState();
        if (!socket) {
          console.log('Socket was lost, re-initializing');
          initSocket();
        } else if (!socket.connected) {
          // Tear down and re-init with a fresh token — the existing socket
          // may hold an expired auth token if the app was backgrounded long enough.
          console.log('Socket disconnected, cleaning up and initializing new socket');
          cleanUpSocket();
          initSocket();
        }
      }
    };

    const subscription = AppState.addEventListener('change', handler);
    return () => subscription.remove();
  }, []);

  useEffect(() => {
    cleanupExpiredImages().catch(() => {});
  }, []);

  useEffect(() => {
    if (!needsManualAuth) {
      useSocketStore.getState().initSocket().catch(console.error);
    }
  }, [needsManualAuth]);

  useEffect(() => {
    if (sessionTakenOver) trackView(MetricView.SessionTakenOver);
  }, [sessionTakenOver]);

  if (sessionTakenOver) {
    return (
      <View style={takenOverStyles.root}>
        <Text style={takenOverStyles.heading}>App opened in another instance</Text>
        <Text style={takenOverStyles.body}>
          This instance has been disconnected because the app was opened in another location.
        </Text>
      </View>
    );
  }

  if (needsManualAuth) {
    return <TokenPrompt onSubmit={() => setNeedsManualAuth(false)} />;
  }

  if (!ftueLoaded) {
    return null;
  }

  // An unfinished FTUE always shows it, except over an active deep link.
  const showingFTUE = (needsFTUE || route.name === 'ftue') && !onDeepLink;
  const renderRoute = () => {
    if (showingFTUE) return <FTUEFlow />;

    switch (route.name) {
      case 'gallery':
        return <Gallery />;
      case 'sharedGallery':
        // Sharing disabled — never surface the shared view (e.g. stale deep link).
        return sharingEnabled ? <SharedGallery /> : <Gallery />;
      case 'reader': {
        const p = route.params as RouteParams['reader'];
        // Shared reader only when sharing is enabled — a stale shared deep link
        // otherwise stalls on a preview that never arrives. Fall back to gallery.
        if (p.shared) {
          return sharingEnabled ? <ComicViewer sharedComicId={p.comic} /> : <Gallery />;
        }
        return <ComicViewer comicId={p.comic} />;
      }
      case 'builder':
        return <StoryBuilder />;
      case 'coder':
        return <CoderPage />;
      case 'gameLab':
        return <GameLabScreen />;
      case 'gameTemplates':
        return <GameTemplateScreen />;
      case 'animator':
        return <AnimatorPage />;
      case 'landing':
      default:
        return (
          <LandingPage
            onCreateNew={() => startBuilderSession({ fresh: true })}
            onResume={() => useSocketStore.getState().resumeStory()}
            onGallery={() => navigate('gallery')}
          />
        );
    }
  };

  // Error toast and access banner are global chrome owned here (not per-screen),
  // so they don't duplicate across the route content.
  const toastVariant = DARK_THEME_ROUTES.has(route.name) ? 'dark' : 'light';

  // Opaque app background so no transition frame reveals the native splash behind the RN view.
  return (
    <View style={appStyles.root}>
      {renderRoute()}
      <ErrorToast variant={toastVariant} />
      {accessBlocked && !showingFTUE && <GrownUpsBanner title={ACCESS_BLOCKED_TITLES[accessBlocked]} />}
    </View>
  );
}

const appStyles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.bg },
});

const takenOverStyles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.bg,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  heading: {
    fontSize: 20,
    ...textStyle.heading,
    color: colors.text,
    marginBottom: 12,
    textAlign: 'center',
  },
  body: {
    fontSize: 15,
    color: colors.textMuted,
    ...textStyle.body,
    textAlign: 'center',
    maxWidth: 400,
    lineHeight: 22,
  },
});
